/**
 * Fair Value Workbooks — All Documents section panel.
 *
 * The EY embedded-derivative (PPA fair value) workbook family: completely
 * separate from the RevFM financial models (own parser, own registry,
 * never touches budget/covenant tables). Uploading here is how a new
 * measurement period lands in the platform — the workbook is validated on
 * the way in (every stated valuation recomputed, the day-1 calibration
 * factor re-solved) and the latest upload becomes the source for the
 * PPAs & Contracts → Fair Value tab.
 *
 * Mirrors the FinancialModels panel contract: {portfolioId, embedded,
 * onCountChange}.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Alert, Chip, CircularProgress, Link,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BalanceIcon from '@mui/icons-material/Balance';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const fmtAud = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  const s = v < 0 ? '−' : '';
  if (a >= 1e6) return `${s}A$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${s}A$${(a / 1e3).toFixed(0)}k`;
  return `${s}A$${a.toFixed(0)}`;
};
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleDateString('en-AU', {
  day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const headerCellSx = {
  fontWeight: 700, fontSize: '0.78rem', color: 'text.secondary',
  textTransform: 'uppercase', letterSpacing: '0.04em',
};

export default function FairValueWorkbooks({ portfolioId, embedded = false,
                                             onCountChange }) {
  const navigate = useNavigate();
  const [workbooks, setWorkbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [toDelete, setToDelete] = useState(null);   // workbook row pending delete
  const [deleting, setDeleting] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/portfolios/${portfolioId}/fair-value-workbooks`);
      const list = r.data?.workbooks || [];
      setWorkbooks(list);
      onCountChange?.(list.length);
    } catch {
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, onCountChange]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setMsg(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(
        `${API}/api/portfolios/${portfolioId}/fair-value-workbooks/upload`,
        form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const secs = r.data?.sections || [];
      const ok = secs.every((s) => s.recompute_diff_aud == null
                                   || Math.abs(s.recompute_diff_aud) < 1);
      const factor = r.data?.factor_check?.solved_factor;
      setMsg({
        severity: ok ? 'success' : 'warning',
        text: ok
          ? `Parsed — ${secs.length} measurement-date valuations replicated`
            + (factor ? `, day-1 factor ${(factor * 100).toFixed(2)}% verified.` : '.')
          : 'Parsed, but some valuations did not replicate exactly — open the Fair Value tab to inspect.',
      });
      fetchList();
    } catch (err) {
      setMsg({ severity: 'error',
               text: err.response?.data?.error || err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/api/portfolios/${portfolioId}/fair-value-workbooks/${toDelete.id}`);
      setMsg({ severity: 'success',
               text: `Deleted "${toDelete.source_filename}".` });
      setToDelete(null);
      fetchList();
    } catch (err) {
      setMsg({ severity: 'error',
               text: err.response?.data?.error || err.message });
    } finally {
      setDeleting(false);
    }
  };

  const latestSummary = (wb) => {
    const secs = wb.summary_json?.sections || [];
    return secs.length ? secs[secs.length - 1] : null;
  };

  return (
    <Box sx={{ mt: embedded ? 0 : 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        {!embedded && (
          <>
            <BalanceIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Fair Value Workbooks
            </Typography>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button component="label" size="small" variant="outlined"
                startIcon={uploading ? <CircularProgress size={14} />
                                     : <UploadFileIcon />}
                disabled={uploading}>
          {uploading ? 'Parsing…' : 'Upload fair value workbook'}
          <input type="file" hidden accept=".xlsx,.xlsm" onChange={handleUpload} />
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        EY embedded-derivative (PPA fair value) workbooks. Each upload is
        validated against its own stated valuations; the latest drives the
        {' '}
        <Link component="button" variant="body2" sx={{ verticalAlign: 'baseline' }}
              onClick={() => navigate(`/portfolios/${portfolioId}/ppas/fair-value`)}>
          Fair Value
        </Link>{' '}tab.
      </Typography>

      {msg && (
        <Alert severity={msg.severity} sx={{ mb: 1.5 }}
               onClose={() => setMsg(null)}>{msg.text}</Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={22} />
        </Box>
      ) : workbooks.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No fair value workbook uploaded yet — the Fair Value tab falls
            back to the model-based estimate until one is.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}
                        sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(217, 138, 43, 0.08)' }}>
                <TableCell sx={headerCellSx}>Filename</TableCell>
                <TableCell sx={headerCellSx}>Latest valuation</TableCell>
                <TableCell sx={headerCellSx}>Fair value</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx}>Uploaded</TableCell>
                <TableCell sx={headerCellSx} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workbooks.map((wb, i) => {
                const latest = latestSummary(wb);
                const isCurrent = i === 0 && wb.status === 'parsed';
                return (
                  <TableRow key={wb.id} hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(
                              `/portfolios/${portfolioId}/ppas/fair-value`)}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {wb.source_filename}
                        {isCurrent && (
                          <Chip size="small" color="success" variant="outlined"
                                label="current" sx={{ height: 20 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {latest
                        ? `${latest.label?.replace(/^At\s+/i, '')}`
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600,
                                     color: latest?.fair_value_aud > 0
                                       ? '#1f9d57'
                                       : latest?.fair_value_aud < 0
                                         ? '#e5384f' : 'inherit' }}>
                      {fmtAud(latest?.fair_value_aud)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined"
                            color={wb.status === 'parsed' ? 'success' : 'default'}
                            label={wb.status} sx={{ height: 20 }} />
                    </TableCell>
                    <TableCell>{fmtWhen(wb.created_at)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setToDelete(wb); }}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'error.main' },
                            transition: 'color 0.2s ease',
                          }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete fair value workbook</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Delete "{toDelete?.source_filename}"? This removes the workbook and
            its parsed valuations. {workbooks[0]?.id === toDelete?.id
              && workbooks.length > 1
              ? 'The next most recent workbook becomes the source for the Fair Value tab.'
              : workbooks.length === 1
                ? 'The Fair Value tab will fall back to the model-based estimate.'
                : ''} This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained"
                  disabled={deleting}
                  startIcon={deleting ? <CircularProgress size={14} /> : null}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
